if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      constructor() {
        super();

        this.checkboxes = Array.from(this.querySelectorAll('[data-fbt-checkbox]'));
        this.totalEl = this.querySelector('[data-fbt-total]');
        this.errorEl = this.querySelector('[data-fbt-error]');
        this.submitButton = this.querySelector('[data-fbt-submit]');
        this.submitLabel = this.querySelector('[data-fbt-submit-label]');
        this.addAllTemplate = this.dataset.addAllTemplate || '';
        this.addOneLabel = this.dataset.addOneLabel || '';

        const totalsScript = this.querySelector('[data-fbt-totals]');
        this.totals = totalsScript ? JSON.parse(totalsScript.textContent) : {};
        const centsScript = this.querySelector('[data-fbt-totals-cents]');
        this.totalsCents = centsScript ? JSON.parse(centsScript.textContent) : null;
        this.slot0Price = Number(this.dataset.slot0Price);
        this.slot0Offset = 0;
        this.moneyFormat = this.dataset.moneyFormat || '';
        try {
          this.variantIds = JSON.parse(this.dataset.productVariantIds || '[]');
        } catch (error) {
          this.variantIds = [];
        }

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', this.updateState.bind(this));
        });

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        }

        this.updateState();
      }

      connectedCallback() {
        // Follow the main product's variant picker, so "This item" is always
        // the variant the shopper chose rather than the first one rendered.
        // Only this product's own variants count: a quick-add on another
        // product publishes the same event.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.variantUnsubscriber) {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) {
          this.variantUnsubscriber();
          this.variantUnsubscriber = undefined;
        }
      }

      onVariantChange(event) {
        const variant = event && event.data ? event.data.variant : null;
        if (!variant || !this.variantIds.includes(variant.id)) return;
        const slot0 = this.checkboxes.find((checkbox) => checkbox.dataset.fbtSlot === '0');
        if (!slot0) return;

        slot0.dataset.fbtVariantId = String(variant.id);
        // An unavailable variant cannot be added, so it is taken out of the
        // bundle rather than silently replaced with a different one.
        slot0.disabled = variant.available === false;
        if (variant.available === false) slot0.checked = false;

        if (Number.isFinite(variant.price) && Number.isFinite(this.slot0Price) && this.totalsCents && this.moneyFormat) {
          this.slot0Offset = variant.price - this.slot0Price;
          const priceEl = this.querySelector('[data-fbt-slot0-price]');
          if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
          Object.keys(this.totalsCents).forEach((key) => {
            const cents = this.totalsCents[key] + (key.charAt(0) === '1' ? this.slot0Offset : 0);
            this.totals[key] = this.formatMoney(cents);
          });
        }
        this.updateState();
      }

      /** Shopify's money-format placeholders, for the shop's own format string. */
      formatMoney(cents) {
        const format = this.moneyFormat;
        const withDelimiters = (amount, precision, thousands, decimal) => {
          const fixed = (amount / 100).toFixed(precision);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts[1] ? whole + decimal + parts[1] : whole;
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return withDelimiters(cents, 2, ',', '.');
        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(cents, 2, "'", '.');
            break;
          default:
            value = withDelimiters(cents, 2, ',', '.');
        }
        return format.replace(match[0], value);
      }

      getComboKey() {
        const bits = ['0', '0', '0', '0'];
        this.checkboxes.forEach((checkbox) => {
          const slot = parseInt(checkbox.dataset.fbtSlot, 10);
          bits[slot] = checkbox.checked ? '1' : '0';
        });
        return bits.join('');
      }

      updateState() {
        const key = this.getComboKey();
        const total = this.totals[key];
        if (this.totalEl && total !== undefined) this.totalEl.textContent = total;

        const checkedCount = this.checkboxes.filter((checkbox) => checkbox.checked).length;
        if (this.submitButton) {
          this.submitButton.disabled = checkedCount === 0;
        }
        if (this.submitLabel) {
          this.submitLabel.textContent =
            checkedCount === 1 ? this.addOneLabel : this.addAllTemplate.replace('__COUNT__', checkedCount);
        }
      }

      handleError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = !message;
        this.errorEl.textContent = message || '';
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.disabled) return;

        const items = this.checkboxes
          .filter((checkbox) => checkbox.checked)
          .map((checkbox) => ({ id: parseInt(checkbox.dataset.fbtVariantId, 10), quantity: 1 }));

        if (!items.length) return;

        this.handleError(false);
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');

        const config = fetchConfig('json');
        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'frequently-bought-together',
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleError(response.description || response.message);
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'frequently-bought-together',
              cartData: response,
            });
            // A multi-item /cart/add.js response has no top-level `key` (only a
            // single-item response does), but cart-notification's renderContents
            // needs one to select which added line to feature in the popup.
            const lastItem = Array.isArray(response.items) ? response.items[response.items.length - 1] : null;
            this.cart.renderContents(lastItem ? { ...response, key: lastItem.key } : response);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          })
          .catch((error) => {
            console.error(error);
            this.handleError(window.cartStrings ? window.cartStrings.error : 'Network error');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.updateState();
          });
      }
    }
  );
}
